<script setup>
import { computed } from 'vue'

const props = defineProps({
  // A retailer/offer row: { id, retailerName, logo, banner, highestCashbackValue,
  // highestCashbackType, offerTypes }
  offer: { type: Object, required: true },
  // Site option (featureFlags.showOfferBanners): render the offer's banner image on the card.
  showBanner: { type: Boolean, default: false },
})
defineEmits(['select'])

const monogram = computed(() => (props.offer.retailerName || '?').charAt(0).toUpperCase())

const cashbackLabel = computed(() => {
  const v = props.offer.highestCashbackValue
  if (v == null) return ''
  const type = String(props.offer.highestCashbackType || '')
  return type.includes('PERCENTAGE') ? `${v}% cashback` : `£${v} cashback`
})
</script>

<template>
  <div
    class="wl-offer-card"
    :class="{ 'has-banner': showBanner && offer.banner }"
    role="button"
    tabindex="0"
    @click="$emit('select', offer)"
    @keydown.enter="$emit('select', offer)"
  >
    <img
      v-if="showBanner && offer.banner"
      class="wl-banner"
      :src="offer.banner"
      :alt="`${offer.retailerName} offer`"
      loading="lazy"
    />
    <div class="wl-offer-row">
      <div class="wl-logo">
        <img v-if="offer.logo" :src="offer.logo" :alt="offer.retailerName" />
        <span v-else class="wl-monogram">{{ monogram }}</span>
      </div>
      <div class="wl-offer-info">
        <div class="wl-name">{{ offer.retailerName }}</div>
        <div v-if="cashbackLabel" class="wl-cashback">{{ cashbackLabel }}</div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.wl-offer-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: color-mix(in srgb, var(--brand-text, #1f2937) 4%, var(--brand-bg, #fff));
  border: 1px solid var(--brand-border, transparent);
  border-radius: var(--brand-radius, 14px);
  padding: 12px 14px;
  cursor: pointer;
  box-shadow: var(--brand-shadow, none);
  transition:
    transform 0.12s,
    box-shadow 0.12s;
}
/* Banner variant: image header on top, the usual row beneath it. */
.wl-offer-card.has-banner {
  flex-direction: column;
  align-items: stretch;
  gap: 0;
  padding: 0;
  overflow: hidden;
}
.wl-banner {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
}
.wl-offer-row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;
}
.has-banner .wl-offer-row {
  padding: 12px 14px;
}
.wl-offer-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 24px -16px rgba(0, 0, 0, 0.35);
}
.wl-offer-card:focus-visible {
  outline: 2px solid var(--brand-primary, #6b7280);
  outline-offset: 2px;
}
.wl-logo {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  /* Rounded even on square themes. */
  border-radius: max(10px, calc(var(--brand-radius, 14px) * 0.7));
  background: var(--brand-bg, #fff);
  overflow: hidden;
}
.wl-logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: inherit;
}
.wl-monogram {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: var(--brand-primary, #6b7280);
  color: var(--brand-button-text, #fff);
  font-weight: 800;
  font-size: 1.1rem;
}
.wl-offer-info {
  flex: 1;
  min-width: 0;
}
.wl-name {
  font-weight: 700;
  font-size: 0.95rem;
  color: var(--brand-text, #1f2937);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.wl-cashback {
  margin-top: 2px;
  font-size: 0.95rem;
  color: color-mix(in srgb, var(--brand-text, #1f2937) 75%, transparent);
}
</style>
